import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Divider, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import EditIcon from '@mui/icons-material/Edit';
import moment, { isMoment } from 'moment';
import _ from 'lodash';

import useNotification from '../../../../../core/hooks/use_notification';
import { Button, SubmitButton } from '../../../../common/button';
import TextFieldFormik from '../../../../common/text_field';
import TimePickerFormik from '../../../../common/time_picker';
import { parseDateString } from '../../../../../core/utils/date_utils';
import { yesNoOptions } from '../../../../common/form_utils';
import RadioGroupFormik from '../../../../common/radio_group';
import { SelectFieldMultipleFormik, SelectFieldFormik } from '../../../../common/select_field';
import { getConstantsAction, getConstantsState } from '../../../../../features/product/get_constants';
import { getConstantsNotificationData, getStorePromotionSettingNotificationData, updateStorePromotionSettingNotificationData, getStoreDiscountTypesNotificationData } from '../../../../../core/utils/notification_utils';
import { getStorePromotionSettingAction, getStorePromotionSettingState } from '../../../../../features/price/get_store_promotion_setting';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { MISC_DATE_ISO_TIME_ONLY_FORMAT } from '../../../../../core/constants';
import { updateStorePromotionSettingAction, updateStorePromotionSettingState } from '../../../../../features/price/update_store_promotion_setting';
import { getStoreDiscountTypesAction, getStoreDiscountTypesState } from '../../../../../features/price/get_store_discount_types';

const UpdateStorePromotionSettingDialog = ({ refreshList, type, podId, podName }) => {
    const dispatch = useDispatch();
    const { showErrorMessage, showNotification } = useNotification();

    const getStorePromotionSetting = useSelector(getStorePromotionSettingState);
    const getConstants = useSelector(getConstantsState);
    const updateStorePromotionSetting = useSelector(updateStorePromotionSettingState);
    const getStoreDiscountTypes = useSelector(getStoreDiscountTypesState);

    const [open, setOpen] = useState(false);
    const [metaFlags, setMetaFlags] = useState({
        supportsAdvancedDiscountConfiguration: false,
        supportsProductTypes: false,
        requiresDiscountValue: false
    });

    const [initialValues, setInitialValues] = useState(
        {
            timeFrom: '',
            active: false,
            discountValue: 0,
            discountType: '',
            productTypeIds: [],
            type: type
        }
    );

    const schema = Yup.object().shape({
        timeFrom: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required('Czas rozpoczęcia jest wymagany'),
        active: Yup.boolean().required(),
        discountValue: Yup.number().when([], {
            is: () => metaFlags.requiresDiscountValue,
            then: Yup.number().required('Wartość obniżki jest wymagana').min(0.01, 'Musi być większa od 0'),
            otherwise: Yup.number().notRequired()
        }),
        discountType: Yup.string().when([], {
            is: () => metaFlags.supportsAdvancedDiscountConfiguration,
            then: Yup.string().required('Sposób zniżki jest wymagany'),
            otherwise: Yup.string().notRequired()
        }),
        productTypeIds: Yup.array().when([], {
            is: () => metaFlags.supportsProductTypes,
            then: Yup.array().min(1, 'Wybierz co najmniej jeden typ produktu'),
            otherwise: Yup.array().notRequired()
        }),
    });

    useEffect(() => {
        if (open) {
            dispatch(getConstantsAction())
                .then(response => showErrorMessage(getConstantsNotificationData(response)));
            dispatch(getStoreDiscountTypesAction({ languageCode: 'PL' }))
                .then(response => showErrorMessage(getStoreDiscountTypesNotificationData(response)));
            dispatch(getStorePromotionSettingAction({ type, podId }))
                .then(response => {
                    showErrorMessage(getStorePromotionSettingNotificationData(response));
                    response?.meta?.requestStatus === ApiRequestStatusEnum.REJECTED && onClose();

                    const setting = response?.payload;

                    setMetaFlags({
                        supportsAdvancedDiscountConfiguration: setting?.supportsAdvancedDiscountConfiguration || false,
                        supportsProductTypes: setting?.supportsProductTypes || false,
                        requiresDiscountValue: setting?.requiresDiscountValue || false,
                    });

                    setInitialValues(prevState => ({
                        ...prevState,
                        timeFrom: setting?.timeFrom ? moment(setting?.timeFrom, MISC_DATE_ISO_TIME_ONLY_FORMAT) : '',
                        active: setting?.active || false,
                        discountValue: setting?.discountValue || 0,
                        discountType: setting?.discountType || '',
                        productTypeIds: setting?.productTypeIds || []
                    }));
                });
        }
    }, [dispatch, open, podId, showErrorMessage, type]);

    const onClose = () => setOpen(false);

    const onSubmit = (values) => {
        const timeFrom = isMoment(_.pick(values, ['timeFrom'])?.timeFrom) ? _.pick(values, ['timeFrom'])?.timeFrom?.format(MISC_DATE_ISO_TIME_ONLY_FORMAT) : '';

        const form = {
            ..._.omit(values, [
                'timeFrom',
                (!metaFlags.requiresDiscountValue) && 'discountValue',
                (!metaFlags.supportsAdvancedDiscountConfiguration) && 'discountType',
                (!metaFlags.supportsProductTypes) && 'productTypeIds',
            ]),
            timeFrom,
            podId
        };

        dispatch(updateStorePromotionSettingAction(form))
            .then(response => {
                showNotification(updateStorePromotionSettingNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    refreshList && refreshList();
                    onClose();
                }
            });
    };

    return (
        <>
            <Tooltip title="Edytuj zasady i czas">
                <IconButton size="small" color="primary" onClick={ () => setOpen(true) }>
                    <EditIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="sm">
                <Formik
                    initialValues={ initialValues }
                    enableReinitialize
                    validationSchema={ schema }
                    validateOnChange={ false }
                    validateOnBlur={ false }
                    onSubmit={ onSubmit }>
                    { () => (
                        <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <DialogTitle>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        Ustawienia promocji sklepowej
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Punkt: { podName }
                                    </Typography>
                                </Box>
                            </DialogTitle>
                            <Divider/>

                            <DialogContent>
                                {
                                    (getConstants?.loading || getStorePromotionSetting?.loading || getStoreDiscountTypes?.loading) &&
                                    <Box m={ 3 } width="100%">
                                        <LinearProgress/>
                                    </Box>
                                }
                                {
                                    (getConstants?.data && getStorePromotionSetting?.data && getStoreDiscountTypes?.data) &&
                                    <Stack spacing={ 4 } sx={ { mt: 1 } }>
                                        <RadioGroupFormik
                                            name="active"
                                            label="Aktywny"
                                            options={ yesNoOptions }
                                        />

                                        {
                                            metaFlags.supportsAdvancedDiscountConfiguration && (
                                                <SelectFieldFormik
                                                    name="discountType"
                                                    label="Sposób wyliczania zniżki"
                                                    options={ getStoreDiscountTypes.data }
                                                    required
                                                />
                                            )
                                        }

                                        {
                                            metaFlags.requiresDiscountValue && (
                                                <TextFieldFormik
                                                    name="discountValue"
                                                    label="Wartość zniżki"
                                                    type="number"
                                                    required
                                                    inputProps={ { min: '0.01', max: '1000.00', step: '0.01' } }
                                                />
                                            )
                                        }

                                        <TimePickerFormik
                                            name="timeFrom"
                                            required
                                            label="Czas rozpoczęcia"
                                            InputLabelProps={ { shrink: true } }
                                            views={ ['hours', 'minutes'] }
                                            fullWidth
                                        />

                                        {
                                            metaFlags.supportsProductTypes && (
                                                <SelectFieldMultipleFormik
                                                    name="productTypeIds"
                                                    label="Typy produktów"
                                                    options={ getConstants?.data?.productConstants?.FOOD_TYPE?.map(productConstant => ({
                                                        value: productConstant?.id,
                                                        label: productConstant?.body
                                                    })) }
                                                    required
                                                />
                                            )
                                        }
                                    </Stack>
                                }
                            </DialogContent>

                            {
                                (getConstants?.data && getStorePromotionSetting?.data && getStoreDiscountTypes?.data) && (
                                    <>
                                        <Divider/>
                                        <DialogActions sx={ { px: 3, py: 2, gap: 2 } }>
                                            <Button variant="outlined" onClick={ onClose }>Anuluj</Button>
                                            <SubmitButton isLoading={ updateStorePromotionSetting?.loading }>
                                                Zapisz
                                            </SubmitButton>
                                        </DialogActions>
                                    </>
                                )
                            }
                        </Box>
                    ) }
                </Formik>
            </Dialog>
        </>
    );
};

export default UpdateStorePromotionSettingDialog;